import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  api,
  ApiError,
  setAccessToken,
  setImpersonationActive,
  setNativeRefreshToken,
  setOnImpersonationExpired,
  setOnSessionExpired,
} from '../api';
import { IS_NATIVE } from '../base';
import {
  clearBiometricToken,
  getRefreshTokenWithBiometrics,
  saveRefreshToken,
} from '../native/biometricAuth';
import { t } from '../i18n';
import type {
  Company,
  CompanyMembership,
  CompanySelectionRequired,
  GoogleAuthResponse,
  GoogleLoginResult,
  ImpersonateResponse,
  LoginResponse,
  LoginResult,
  MeResponse,
  RegisterPayload,
  RegisterResponse,
  User,
} from './types';

/** Deriva una membresía (id/nombre/rol) del user+company de una sesión. */
function membershipFrom(user: User | null, company: Company | null): CompanyMembership[] {
  if (!company) return [];
  return [{ id: company.id, name: company.name, role: user?.role ?? 'MEMBER' }];
}

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: SessionStatus;
  user: User | null;
  company: Company | null;
  /** Todas las empresas del usuario (membresía multi-empresa). Vacío si es una sola. */
  companies: CompanyMembership[];
  isAuthenticated: boolean;
  /**
   * Login por email. Si el usuario tiene 1 empresa, establece la sesión. Si tiene
   * >1, NO establece sesión: deja `pendingCompanies` con las opciones y devuelve
   * `{ needsCompanySelection: true }` para que la pantalla muestre el paso de
   * selección (la sesión se cierra con `selectCompany`).
   */
  login: (email: string, password: string, captchaToken?: string) => Promise<LoginResult>;
  register: (payload: RegisterPayload) => Promise<void>;
  /**
   * Login con Google (ID token de GIS). Si el email ya tiene cuenta activa,
   * establece la sesión; si no, devuelve `{ needsRegistration:true, email, name }`
   * para que la pantalla lleve al registro con el email precargado.
   */
  loginWithGoogle: (credential: string) => Promise<GoogleLoginResult>;
  logout: () => Promise<void>;
  /**
   * Desbloqueo con biometría (SÓLO app nativa). Pide Face ID / Touch ID / huella,
   * recupera el refresh token del Keychain/Keystore, lo canjea por una sesión y
   * la establece. Devuelve `true` si quedó autenticado; `false` si el usuario
   * canceló, no hay token guardado o el refresh falló. No-op en web.
   */
  loginWithBiometrics: () => Promise<boolean>;
  /**
   * Establece la sesión a partir de una respuesta con token+user+company
   * (mismo shape que register). Lo usa el alta por invitación para quedar
   * logueado sin pasar por login.
   */
  establishSession: (res: RegisterResponse) => void;
  /** Refresca el `user` en memoria tras editar el perfil (PATCH /me). */
  updateUser: (user: User) => void;
  /** ¿La sesión actual es una impersonación de soporte? */
  isImpersonating: boolean;
  /**
   * Inicia una impersonación: cambia la sesión al token efímero devuelto por
   * el backend (user+company de la empresa objetivo). NO toca la cookie de
   * refresh del super admin (queda intacta para volver).
   */
  startImpersonation: (res: ImpersonateResponse) => void;
  /**
   * Termina la impersonación y rehidrata la sesión del super admin desde su
   * cookie de refresh (que nunca se tocó). Devuelve `true` si volvió OK.
   */
  stopImpersonation: () => Promise<boolean>;

  // --- Membresía multi-empresa ---------------------------------------------
  /**
   * Empresas a elegir tras un login con >1 empresa (selección pendiente). `null`
   * cuando no hay selección en curso. La pantalla de login muestra el paso de
   * selección mientras esto no sea null.
   */
  pendingCompanies: CompanyMembership[] | null;
  /**
   * Canjea el `select_token` pendiente por la sesión de la empresa elegida
   * (POST /auth/select-company) y la establece (igual que un login normal).
   * Lanza ApiError('not_member') o 401 si el token expiró → la pantalla debe
   * llamar `cancelSelection()` y pedir reingreso.
   */
  selectCompany: (companyId: string) => Promise<void>;
  /** Descarta la selección pendiente y vuelve al formulario de login. */
  cancelSelection: () => void;
  /**
   * Cambia la empresa activa SIN desloguear (POST /auth/switch-company): rota el
   * refresh, re-hidrata la sesión y limpia TODA la caché de react-query (los
   * datos son de otro tenant). Devuelve la empresa a la que se cambió.
   */
  switchCompany: (companyId: string) => Promise<CompanyMembership>;
  /**
   * Re-lee /auth/me y actualiza user/company/companies SIN tocar tokens. Útil
   * cuando la membresía cambió del lado del server (ej. sumarse a una empresa
   * nueva por invitación estando ya logueado) y hay que reflejar `companies`.
   */
  reloadMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provee el estado de sesión a toda la app.
 *
 * - `accessToken` vive en memoria dentro de lib/api (no en este componente).
 * - Al montar, `bootstrap()` intenta /auth/refresh (cookie httpOnly) y, si hay
 *   sesión, hidrata user+company con /auth/me.
 * - Registra `onSessionExpired` en el cliente HTTP para que un refresh fallido
 *   (durante cualquier request) limpie la sesión y dispare el redirect a login.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<CompanyMembership[]>([]);
  const [isImpersonating, setIsImpersonating] = useState(false);
  // Selección pendiente tras un login con >1 empresa: token efímero (en memoria,
  // NUNCA en localStorage) + empresas a elegir. Se limpia al elegir/cancelar.
  const [pendingSelection, setPendingSelection] = useState<{
    selectToken: string;
    companies: CompanyMembership[];
  } | null>(null);
  const bootstrapped = useRef(false);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setImpersonationActive(false);
    setIsImpersonating(false);
    setUser(null);
    setCompany(null);
    setCompanies([]);
    setPendingSelection(null);
    setStatus('unauthenticated');
    // Toda la data cacheada es del usuario/empresa saliente. Si no la tiramos,
    // un login posterior con OTRA cuenta mostraría los datos del anterior hasta
    // un refresh (las query keys no están scopeadas por identidad). Es el único
    // choke point de "fin de sesión" (logout, expiración, refresh fallido).
    queryClient.clear();
  }, [queryClient]);

  // Un refresh transparente que falla → sesión vencida.
  useEffect(() => {
    setOnSessionExpired(() => {
      clearSession();
    });
    return () => setOnSessionExpired(null);
  }, [clearSession]);

  // Bootstrap único al cargar la app.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    // Nativo: NO auto-refrescamos en silencio. El refresh token vive en el
    // Keychain/Keystore gated por biometría (no en una cookie httpOnly que el
    // WebView cross-origin no manda). Dejamos la sesión sin iniciar; el
    // LoginPage ofrece el botón de desbloqueo biométrico si hay token guardado.
    if (IS_NATIVE) {
      clearSession();
      return;
    }

    (async () => {
      try {
        // Rehidrata el access token desde la cookie de refresh.
        const refreshed = await api.post<{ access_token: string }>(
          '/auth/refresh',
          undefined,
          { skipAuthRefresh: true },
        );
        setAccessToken(refreshed.access_token);
        const me = await api.get<MeResponse>('/auth/me');
        setUser(me.user);
        setCompany(me.company);
        setCompanies(me.companies ?? membershipFrom(me.user, me.company));
        setStatus('authenticated');
      } catch {
        clearSession();
      }
    })();
  }, [clearSession]);

  const login = useCallback(
    async (email: string, password: string, captchaToken?: string): Promise<LoginResult> => {
      const res = await api.post<LoginResponse | CompanySelectionRequired>(
        '/auth/login',
        // captcha_token undefined se omite del JSON → el backend lo trata como
        // "sin captcha" (OK si el feature está off).
        { email, password, captcha_token: captchaToken },
        { skipAuthRefresh: true },
      );
      // >1 empresa: no hay sesión todavía. Guardamos el token efímero + opciones
      // y dejamos que la pantalla muestre el paso de selección. La clave sólo
      // viene cuando hace falta elegir → su presencia narrowea el tipo.
      if ('needs_company_selection' in res) {
        setPendingSelection({ selectToken: res.select_token, companies: res.companies });
        return { needsCompanySelection: true };
      }
      setAccessToken(res.access_token);
      // Nativo: el login trae el refresh token en el body → lo guardamos en secure
      // storage (gated por biometría) para ofrecer desbloqueo en el próximo ingreso,
      // y lo mantenemos en memoria para los refresh transparentes de esta sesión.
      if (IS_NATIVE && res.refresh_token) {
        setNativeRefreshToken(res.refresh_token);
        void saveRefreshToken(res.refresh_token);
      }
      // login no trae company → la traemos con /auth/me.
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
      setCompanies(me.companies ?? membershipFrom(me.user, me.company));
      setPendingSelection(null);
      setStatus('authenticated');
      return { needsCompanySelection: false };
    },
    [],
  );

  const loginWithGoogle = useCallback(async (credential: string): Promise<GoogleLoginResult> => {
    const res = await api.post<GoogleAuthResponse & Partial<CompanySelectionRequired>>(
      '/auth/google',
      { credential },
      { skipAuthRefresh: true },
    );

    // El email no tiene cuenta: la pantalla debe ir a /registro con el email.
    if (res.needs_registration) {
      return { needsRegistration: true, email: res.email ?? '', name: res.name ?? '' };
    }

    // >1 empresa: dejamos la selección pendiente (igual que login por email).
    if (res.needs_company_selection && res.select_token && res.companies) {
      setPendingSelection({ selectToken: res.select_token, companies: res.companies });
      return { needsRegistration: false, needsCompanySelection: true };
    }

    // Login OK: mismo flujo que login por email (token + /auth/me para company).
    if (res.access_token) {
      setAccessToken(res.access_token);
      if (IS_NATIVE && res.refresh_token) {
        setNativeRefreshToken(res.refresh_token);
        void saveRefreshToken(res.refresh_token);
      }
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
      setCompanies(me.companies ?? membershipFrom(me.user, me.company));
      setPendingSelection(null);
      setStatus('authenticated');
    }
    return { needsRegistration: false, needsCompanySelection: false };
  }, []);

  const loginWithBiometrics = useCallback(async (): Promise<boolean> => {
    if (!IS_NATIVE) return false;
    // 1) Prompt biométrico → refresh token del Keychain/Keystore.
    const token = await getRefreshTokenWithBiometrics(
      t('auth.biometric.reason'),
      t('auth.biometric.title'),
    );
    if (!token) return false;

    try {
      // 2) Canjeamos el refresh token por una sesión (el body lo inyecta lib/api
      //    a partir del token nativo en memoria).
      setNativeRefreshToken(token);
      const refreshed = await api.post<{ access_token: string; refresh_token?: string }>(
        '/auth/refresh',
        undefined,
        { skipAuthRefresh: true },
      );
      setAccessToken(refreshed.access_token);
      // 3) El refresh rota one-time-use: guardamos el nuevo token.
      if (refreshed.refresh_token) {
        setNativeRefreshToken(refreshed.refresh_token);
        void saveRefreshToken(refreshed.refresh_token);
      }
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
      setCompanies(me.companies ?? membershipFrom(me.user, me.company));
      setStatus('authenticated');
      return true;
    } catch {
      // El token guardado ya no sirve (revocado/rotado/vencido): limpiamos y
      // caemos a login con email + contraseña.
      setNativeRefreshToken(null);
      void clearBiometricToken();
      clearSession();
      return false;
    }
  }, [clearSession]);

  const register = useCallback(async (payload: RegisterPayload) => {
    const res = await api.post<RegisterResponse>('/auth/register', payload, {
      skipAuthRefresh: true,
    });
    setAccessToken(res.access_token);
    // Nativo: guardamos el refresh token recién emitido (gated por biometría)
    // para el próximo ingreso, igual que en login.
    if (IS_NATIVE && res.refresh_token) {
      setNativeRefreshToken(res.refresh_token);
      void saveRefreshToken(res.refresh_token);
    }
    setUser(res.user);
    setCompany(res.company);
    setCompanies(membershipFrom(res.user, res.company));
    setStatus('authenticated');
  }, []);

  const establishSession = useCallback((res: RegisterResponse) => {
    setAccessToken(res.access_token);
    setUser(res.user);
    setCompany(res.company);
    setCompanies(membershipFrom(res.user, res.company));
    setStatus('authenticated');
  }, []);

  const updateUser = useCallback((next: User) => {
    // Fusionamos para no perder campos que el endpoint pudiera no devolver.
    setUser((prev) => (prev ? { ...prev, ...next } : next));
  }, []);

  const startImpersonation = useCallback((res: ImpersonateResponse) => {
    // A partir de acá, el cliente NO debe auto-refrescar (usaría la cookie del
    // super admin y saldría de la impersonación en silencio).
    setImpersonationActive(true);
    setAccessToken(res.access_token);
    setUser(res.user);
    setCompany(res.company);
    // Durante impersonación no ofrecemos switcher: sólo la empresa objetivo.
    setCompanies(membershipFrom(res.user, res.company));
    setIsImpersonating(true);
    setStatus('authenticated');
    // Cambio de identidad (super admin → empresa objetivo): tiramos el cache del
    // contexto anterior para no filtrar datos entre tenants en la UI.
    queryClient.clear();
  }, [queryClient]);

  const stopImpersonation = useCallback(async (): Promise<boolean> => {
    // Reactivamos el refresh y rehidratamos al super admin desde su cookie.
    setImpersonationActive(false);
    try {
      const refreshed = await api.post<{ access_token: string }>('/auth/refresh', undefined, {
        skipAuthRefresh: true,
      });
      setAccessToken(refreshed.access_token);
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
      setCompanies(me.companies ?? membershipFrom(me.user, me.company));
      setIsImpersonating(false);
      setStatus('authenticated');
      // Volvemos al super admin: tiramos el cache de la empresa impersonada.
      queryClient.clear();
      return true;
    } catch {
      // Sin cookie válida no podemos volver: cerramos sesión limpiamente.
      clearSession();
      return false;
    }
  }, [clearSession, queryClient]);

  // El token de impersonación caducó (401 sin refresh) → volvemos al super admin.
  useEffect(() => {
    setOnImpersonationExpired(() => {
      void stopImpersonation();
    });
    return () => setOnImpersonationExpired(null);
  }, [stopImpersonation]);

  // --- Membresía multi-empresa ----------------------------------------------

  const selectCompany = useCallback(
    async (companyId: string) => {
      const sel = pendingSelection;
      if (!sel) {
        // No debería pasar (la UI sólo llama con selección pendiente); fail-safe.
        throw new ApiError(400, 'no_pending_selection', 'No hay selección pendiente.');
      }
      // El select_token va como Bearer explícito (NO hay access token de sesión
      // todavía). skipAuthRefresh: un 401 acá es "token de selección vencido",
      // no una sesión que refrescar.
      const res = await api.post<LoginResponse>(
        '/auth/select-company',
        { company_id: companyId },
        { skipAuthRefresh: true, headers: { Authorization: `Bearer ${sel.selectToken}` } },
      );
      setAccessToken(res.access_token);
      if (IS_NATIVE && res.refresh_token) {
        setNativeRefreshToken(res.refresh_token);
        void saveRefreshToken(res.refresh_token);
      }
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
      setCompanies(me.companies ?? membershipFrom(me.user, me.company));
      setPendingSelection(null);
      setStatus('authenticated');
    },
    [pendingSelection],
  );

  const cancelSelection = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const reloadMe = useCallback(async () => {
    const me = await api.get<MeResponse>('/auth/me');
    setUser(me.user);
    setCompany(me.company);
    setCompanies(me.companies ?? membershipFrom(me.user, me.company));
  }, []);

  const switchCompany = useCallback(
    async (companyId: string): Promise<CompanyMembership> => {
      // Auth normal (access token). Rota el refresh y devuelve una sesión
      // scopeada a la nueva empresa.
      const res = await api.post<LoginResponse>('/auth/switch-company', { company_id: companyId });
      setAccessToken(res.access_token);
      if (IS_NATIVE && res.refresh_token) {
        setNativeRefreshToken(res.refresh_token);
        void saveRefreshToken(res.refresh_token);
      }
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
      const next = me.companies ?? membershipFrom(me.user, me.company);
      setCompanies(next);
      setStatus('authenticated');
      // TODOS los datos cacheados son del tenant anterior → los tiramos para que
      // cada pantalla vuelva a pedir los de la nueva empresa.
      queryClient.clear();
      return (
        next.find((c) => c.id === companyId) ?? {
          id: companyId,
          name: me.company?.name ?? '',
          role: me.user.role,
        }
      );
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipAuthRefresh: true });
    } catch {
      // Aunque falle el server, limpiamos localmente.
    }
    // Nativo: borramos el refresh token del Keychain/Keystore y de memoria para
    // que no quede el atajo biométrico tras cerrar sesión.
    if (IS_NATIVE) {
      setNativeRefreshToken(null);
      void clearBiometricToken();
    }
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      company,
      companies,
      isAuthenticated: status === 'authenticated',
      login,
      register,
      loginWithGoogle,
      logout,
      loginWithBiometrics,
      establishSession,
      updateUser,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
      pendingCompanies: pendingSelection?.companies ?? null,
      selectCompany,
      cancelSelection,
      switchCompany,
      reloadMe,
    }),
    [
      status,
      user,
      company,
      companies,
      login,
      register,
      loginWithGoogle,
      logout,
      loginWithBiometrics,
      establishSession,
      updateUser,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
      pendingSelection,
      selectCompany,
      cancelSelection,
      switchCompany,
      reloadMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}
